import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import CustomerDetailsForm from "../../../components/forms/setup/customers/CustomerDetailsForm";
import { createSnackbarHelpers } from "../../../shared/snackbar";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";

export function CustomersViewModel(logout, addSnackbar) {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState({
    data: [],
  });
  const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const { showSuccess } = createSnackbarHelpers(addSnackbar);

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getCustomers = async function (search, page) {
    setLoading(true);

    let callResult = await makePostApiCall(
      `customers?page=${page}&search=${search}`
    );
    setLoading(false);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const getCustomerTypesForDropdown = async function (search, page) {
    let callResult = await makeGetApiCall(`customerTypesForDropdown`);
    setLoading(false);
    if (callResult.success) {
      setCustomerTypeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const createCustomer = async function (values) {
    const payload = { ...values };
    let callResult = await makePostApiCall(
      "customers/create",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess("Customer added");
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getCustomers("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const updateCustomer = async function (values, index) {
    let callResult = await makePostApiCall(
      `customers/${values.id}`,
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess("Customer updated");
      setSlideOver({
        childComponent: null,
        open: false,
      });

      setTimeout(function () {
        getCustomers("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const openAddCustomerSlideOver = (isEdit, customer, index) => {
    setSlideOver({
      childComponent: (
        <CustomerDetailsForm
          customer={customer}
          customerTypeOptions={customerTypeOptions}
          taxRates={taxRates}
          isEdit={isEdit}
          setOpen={setSlideOver}
          actionCall={(payload) =>
            isEdit ? updateCustomer(payload, index) : createCustomer(payload)
          }
          title={isEdit ? `Update ${customer.name}` : "Add new customer"}
        />
      ),
      open: true,
    });
  };

  const deleteCustomer = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteCustomer/${id}`);
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const newItems = customers.data.filter((item) => item.id !== id);
      setCustomers({ ...customers, data: newItems });

      showSuccess(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteCustomer = function (customer, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteCustomer(customer.id, index)}
          message={`Are you sure you want to delete  ${customer.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  const uploadCustomersCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadCustomersCsv",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      showSuccess(callResult.data.message);
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getCustomers("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportCustomers = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="customersCsv"
          actionCall={uploadCustomersCsv}
          setOpen={setEmptyModelOpen}
          title="Customers"
        />
      ),
      open: true,
    });
  };

  const getTaxRatesForDropdown = async function () {
    let callResult = await makeGetApiCall("taxRatesForDropdown");
    if (callResult.success) {
      setTaxRates(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch tax rates");
    }
  };

  return {
    openAddCustomerSlideOver,
    getCustomers,
    loading,
    customers,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    emptyModal,
    askDeleteCustomer,
    getCustomerTypesForDropdown,
    openImportCustomers,
    getTaxRatesForDropdown,
  };
}
